import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth, adminApi, formatApiError } from "./AuthContext";

const ALLOWED = ["admin", "coordinator"];

export default function ResultsDashboard() {
  const { user, logout } = useAuth();
  const { eventId } = useParams();
  const navigate = useNavigate();

  if (user === null) return <Centered>Loading…</Centered>;
  if (user === false) return <Navigate to="/admin/login" replace />;
  if (!ALLOWED.includes(user.role)) {
    return (
      <Centered>
        <span data-testid="results-forbidden" style={{ color: "#ff6b6b" }}>
          Results are admin / coordinator only.
        </span>
      </Centered>
    );
  }

  return (
    <Inner user={user} logout={logout} eventId={eventId} navigate={navigate} />
  );
}

function Inner({ user, logout, eventId, navigate }) {
  const [events, setEvents] = useState([]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get("/events");
        setEvents(data.items || []);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  const refresh = useCallback(async () => {
    if (!eventId) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/events/${eventId}/results`);
      setData(data);
      setErr("");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    refresh();
    if (!eventId) return;
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [refresh, eventId]);

  const downloadCsv = async (suffix, filenameHint) => {
    try {
      const res = await adminApi.get(`/events/${eventId}/${suffix}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameHint;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div
      data-testid="results-page"
      style={{
        minHeight: "100vh",
        background: "#070D1F",
        color: "#fff",
        fontFamily: "system-ui, sans-serif",
        padding: "28px 28px 56px 28px",
      }}
    >
      <Header user={user} logout={logout} />

      <Picker
        events={events}
        currentId={eventId}
        onSelect={(id) =>
          navigate(id ? `/admin/results/${id}` : "/admin/results")
        }
      />

      {!eventId && (
        <div
          data-testid="results-no-event-selected"
          style={{ opacity: 0.7, marginTop: 24, fontSize: 15 }}
        >
          Pick an event above to view results.
        </div>
      )}

      {err && (
        <div
          data-testid="results-error"
          style={{
            marginTop: 16,
            padding: "10px 14px",
            border: "1px solid #ff6b6b",
            color: "#ff6b6b",
          }}
        >
          {err}
        </div>
      )}

      {eventId && loading && !data && (
        <div style={{ marginTop: 24 }}>Loading…</div>
      )}

      {eventId && data && (
        <>
          <ActionBar
            data={data}
            onExportResults={() =>
              downloadCsv(
                "results.csv",
                `${safe(data.event?.name)}_results.csv`
              )
            }
            onExportAudit={() =>
              downloadCsv(
                "results/audit.csv",
                `${safe(data.event?.name)}_audit.csv`
              )
            }
            onRefresh={refresh}
          />
          <ResultsTable rows={data.rows} />
          <ComputedAt iso={data.computed_at} />
        </>
      )}
    </div>
  );
}

function safe(name) {
  return (name || "event").replace(/\s+/g, "_").replace(/\//g, "_");
}

function Header({ user, logout }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        borderBottom: "1px solid #14213D",
        paddingBottom: 14,
        marginBottom: 22,
      }}
    >
      <div>
        <div style={{ fontSize: 11, opacity: 0.6, letterSpacing: 3 }}>
          THE NEXT STAGE — RESULTS
        </div>
        <div style={{ marginTop: 4, fontSize: 14, opacity: 0.85 }}>
          Organizer view ·{" "}
          <strong data-testid="results-user-name">{user.name}</strong>{" "}
          <span
            style={{
              padding: "2px 8px",
              background: "#E5B25D",
              color: "#0A0F1F",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1,
              marginLeft: 6,
            }}
          >
            {user.role.toUpperCase()}
          </span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <a
          href="/admin"
          style={{
            ...btnSecondary,
            textDecoration: "none",
            display: "inline-block",
            fontSize: 13,
          }}
        >
          ← Admin home
        </a>
        <button onClick={logout} style={btnSecondary}>
          Logout
        </button>
      </div>
    </div>
  );
}

function Picker({ events, currentId, onSelect }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label
        style={{ fontSize: 11, opacity: 0.6, letterSpacing: 2, marginRight: 12 }}
      >
        EVENT
      </label>
      <select
        data-testid="results-event-select"
        value={currentId || ""}
        onChange={(e) => onSelect(e.target.value)}
        style={{
          padding: "10px 12px",
          background: "#0A0F1F",
          color: "#fff",
          border: "1px solid #2a3556",
          fontSize: 14,
          minWidth: 340,
        }}
      >
        <option value="">— pick an event —</option>
        {events.map((e) => (
          <option key={e.id} value={e.id}>
            {e.name} ({e.status}) — {e.event_date}
          </option>
        ))}
      </select>
    </div>
  );
}

function ActionBar({ data, onExportResults, onExportAudit, onRefresh }) {
  return (
    <div
      data-testid="results-action-bar"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 16,
      }}
    >
      <div style={{ fontSize: 13, opacity: 0.7 }}>
        <span style={{ letterSpacing: 2 }}>{(data.event?.name || "").toUpperCase()}</span>
        {data.event?.event_date && (
          <span style={{ marginLeft: 12 }}>· {data.event.event_date}</span>
        )}
        {data.event?.venue && (
          <span style={{ marginLeft: 12 }}>· {data.event.venue}</span>
        )}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          data-testid="results-refresh-btn"
          onClick={onRefresh}
          style={btnSecondary}
        >
          ↻ Refresh
        </button>
        <button
          data-testid="results-export-results-btn"
          onClick={onExportResults}
          style={btnPrimary}
        >
          EXPORT RESULTS CSV
        </button>
        <button
          data-testid="results-export-audit-btn"
          onClick={onExportAudit}
          style={{ ...btnSecondary, borderColor: "#E5B25D", color: "#E5B25D" }}
        >
          EXPORT AUDIT CSV
        </button>
      </div>
    </div>
  );
}

function ResultsTable({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div
        data-testid="results-empty"
        style={{
          marginTop: 24,
          padding: 28,
          border: "1px dashed #2a3556",
          opacity: 0.85,
          textAlign: "center",
        }}
      >
        No speakers in this event yet.
      </div>
    );
  }
  return (
    <table
      data-testid="results-table"
      style={{
        width: "100%",
        borderCollapse: "collapse",
        marginTop: 8,
        fontSize: 14,
      }}
    >
      <thead>
        <tr style={{ textAlign: "left" }}>
          <Th style={{ width: 60 }}>RANK</Th>
          <Th>SPEAKER</Th>
          <Th style={{ width: 100 }}>OVERALL</Th>
          <Th style={{ width: 90 }}>CONTENT</Th>
          <Th style={{ width: 90 }}>DELIVERY</Th>
          <Th style={{ width: 90 }}>IMPACT</Th>
          <Th style={{ width: 90 }}>BALLOTS</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <Row key={r.speaker_id} r={r} />
        ))}
      </tbody>
    </table>
  );
}

function Th({ children, style }) {
  return (
    <th
      style={{
        fontSize: 10,
        letterSpacing: 2,
        opacity: 0.55,
        padding: "10px 12px",
        borderBottom: "1px solid #14213D",
        fontWeight: 600,
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Row({ r }) {
  const winnerBg = r.is_winner
    ? "linear-gradient(90deg,rgba(229,178,93,0.15),rgba(229,178,93,0))"
    : "transparent";
  const noScores = r.n === 0;
  return (
    <tr
      data-testid={`results-row-${r.speaker_id}`}
      style={{
        background: winnerBg,
        borderBottom: "1px solid #14213D",
      }}
    >
      <Td>
        {r.rank ? (
          <span
            style={{
              display: "inline-block",
              minWidth: 32,
              padding: "2px 8px",
              background: r.is_winner ? "#E5B25D" : "rgba(255,255,255,0.06)",
              color: r.is_winner ? "#0A0F1F" : "#fff",
              fontWeight: 800,
              textAlign: "center",
              letterSpacing: 1,
            }}
          >
            {r.rank}
          </span>
        ) : (
          <span style={{ opacity: 0.4 }}>—</span>
        )}
      </Td>
      <Td>
        <div style={{ fontWeight: 700 }}>
          {r.display_name}
          {r.is_winner && (
            <span
              data-testid={`results-winner-flag-${r.speaker_id}`}
              style={{
                marginLeft: 10,
                fontSize: 10,
                letterSpacing: 2,
                padding: "2px 7px",
                background: "#E5B25D",
                color: "#0A0F1F",
                fontWeight: 800,
              }}
            >
              WINNER{r.tied ? " (TIED)" : ""}
            </span>
          )}
          {!r.is_winner && r.tied && (
            <span
              data-testid={`results-tied-flag-${r.speaker_id}`}
              style={{
                marginLeft: 10,
                fontSize: 10,
                letterSpacing: 2,
                padding: "2px 7px",
                background: "rgba(229,178,93,0.18)",
                color: "#E5B25D",
                fontWeight: 700,
              }}
            >
              TIED
            </span>
          )}
        </div>
        {r.talk_title && (
          <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>
            {r.talk_title}
          </div>
        )}
      </Td>
      <Td>
        {noScores ? (
          <NoScoresLabel />
        ) : (
          <span style={{ fontSize: 18, fontWeight: 800 }}>
            {r.overall.toFixed(2)}
          </span>
        )}
      </Td>
      <Td>{noScores ? "—" : r.avg_content.toFixed(2)}</Td>
      <Td>{noScores ? "—" : r.avg_delivery.toFixed(2)}</Td>
      <Td>{noScores ? "—" : r.avg_impact.toFixed(2)}</Td>
      <Td>
        <span
          style={{
            fontWeight: 700,
            color: noScores ? "rgba(255,255,255,0.5)" : "#fff",
          }}
        >
          {r.n}
        </span>
      </Td>
    </tr>
  );
}

function Td({ children }) {
  return (
    <td style={{ padding: "12px", verticalAlign: "middle" }}>{children}</td>
  );
}

function NoScoresLabel() {
  return (
    <span
      data-testid="results-no-scores-yet"
      style={{ fontSize: 12, opacity: 0.6, letterSpacing: 1 }}
    >
      NO SCORES YET
    </span>
  );
}

function ComputedAt({ iso }) {
  if (!iso) return null;
  let denver = "";
  try {
    denver = new Date(iso).toLocaleString("en-US", {
      timeZone: "America/Denver",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      month: "short",
      day: "numeric",
    });
  } catch {
    denver = iso;
  }
  return (
    <div
      data-testid="results-computed-at"
      style={{
        marginTop: 18,
        fontSize: 11,
        opacity: 0.55,
        letterSpacing: 2,
      }}
    >
      COMPUTED {denver} MOUNTAIN TIME · AUTO-REFRESHES EVERY 5S
    </div>
  );
}

function Centered({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#070D1F",
        color: "#fff",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {children}
    </div>
  );
}

const btnPrimary = {
  padding: "10px 14px",
  background: "#E5B25D",
  color: "#0A0F1F",
  border: 0,
  fontWeight: 800,
  letterSpacing: 1,
  cursor: "pointer",
  fontSize: 12,
};
const btnSecondary = {
  padding: "9px 13px",
  background: "transparent",
  color: "#fff",
  border: "1px solid #2a3556",
  cursor: "pointer",
  fontSize: 12,
};
